import { CreatorPage } from "@/components/creators";
import { queryClient } from "@/components/providers";
import {
  creatorsControllerDetailCreator,
  getCreatorsControllerDetailCreatorQueryKey,
} from "@/lib/lutely-api/gen/creators/creators";
import type { DetailCreatorDto } from "@/lib/lutely-api/gen/dtos";
import { CREATOR_ROUTE_DETAIL } from "@/utils/routeUtils";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { z } from "zod";

// `?view=public` opens the profile exactly as a fan sees it, even for its owner
// (who otherwise lands straight in edit mode). Used by "View profile" in the
// Audiolab dashboard, and kept in the URL so a refresh stays on the preview.
// `.catch` rather than a bare `.optional()`: profile URLs get shared and
// decorated with stray params, and a malformed `view` should quietly fall back
// to the normal page instead of erroring the whole route.
const searchSchema = z.object({
  view: z.literal("public").optional().catch(undefined),
});

export const Route = createFileRoute("/@{$CREATOR_SLUG}/")({
  ...CREATOR_ROUTE_DETAIL,
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    try {
      const creator = await queryClient.fetchQuery({
        queryKey: getCreatorsControllerDetailCreatorQueryKey(
          params.CREATOR_SLUG,
        ),
        queryFn: () => creatorsControllerDetailCreator(params.CREATOR_SLUG),
      });
      return { creator };
    } catch {
      return { creator: null as DetailCreatorDto | null };
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { CREATOR_SLUG } = Route.useParams();
  const { view } = useSearch({ from: "/@{$CREATOR_SLUG}/" });
  return (
    <CreatorPage
      key={CREATOR_SLUG}
      creatorIdOrSlug={CREATOR_SLUG}
      forcePublicView={view === "public"}
    />
  );
}
